// ============================================================
// SERVER STATE CLONING
// ============================================================
//
// Pure-function orchestrators receive an immutable `ServerState`
// and return a new one. Internally they clone the input and mutate
// the clone — this avoids spending code on persistent-data-structure
// machinery while still giving callers referential transparency.
//
// BattleUnit uses getter properties (`hp`, `atk`) defined via object
// literals; JSON round-tripping would lose those getters. This module
// therefore reconstructs each unit with the same getter shape as
// `mkUnit` does in `BattleUnit.ts`.

import type { BattleUnit, Base } from '../BattleUnit.ts';
import { UCFG } from '../constants.ts';
import type { ServerState } from './types.ts';

export function cloneUnit(u: BattleUnit): BattleUnit {
  const c = UCFG[u.type];
  if (!c) throw new Error(`cloneUnit: unknown unit type "${u.type}"`);
  const clone: BattleUnit = {
    id: u.id,
    team: u.team,
    type: u.type,
    totalSoldiers: u.totalSoldiers,
    pos: { x: u.pos.x, y: u.pos.y },
    path: u.path.map((p) => ({ x: p.x, y: p.y })),
    pathIdx: u.pathIdx,
    vx: u.vx,
    vy: u.vy,
    flash: u.flash,
    flashColor: u.flashColor,
    buffAtk: u.buffAtk,
    // Session 7 — tick-scoped Shield Wall reductions. Session 10 turned
    // these into cached scalars derived from `modifiers`; we still copy
    // the cached value here so callers that read scalars on the clone
    // see the right number without an extra recompute pass.
    tickDefPhys: u.tickDefPhys ?? 0,
    tickDefMag: u.tickDefMag ?? 0,
    // Session 11 — cached rooted flag derived from `modifiers`. Just copy
    // it (same pattern as the other cached scalars above); a no-op
    // `_recomputeScalars` would arrive at the same answer.
    rooted: !!u.rooted,
    // Session 11 (swamp) — primed-skip flag for speed-1 units on swamp.
    // Direct copy; movement.ts owns the lifecycle (and defaults `true`
    // every off-swamp tick so first swamp encounter always skips).
    swampSkipPending: !!u.swampSkipPending,
    // Session 11 follow-up — cached damage-taken multiplier (defDown sum).
    // Default 1.0 if the source unit somehow lacks it (older fixtures /
    // legacy state).
    dmgTakenMult: typeof u.dmgTakenMult === 'number' && u.dmgTakenMult > 0 ? u.dmgTakenMult : 1,
    // Session 10 — deep-copy modifier list (per-entry shallow clone is
    // enough; primitive fields only). Without this, mutations on the
    // clone (decrement / remove on tick end) would leak into the
    // snapshot the caller still holds.
    modifiers: Array.isArray(u.modifiers)
      ? u.modifiers.map((m) => ({
          kind: m.kind,
          value: m.value,
          ticksRemaining: m.ticksRemaining,
          // Session 10 follow-up: copy startingTicks so the client renderer
          // (which uses ratio = remaining/starting for fade-out) sees a
          // stable baseline even after server-side state snapshots.
          startingTicks: m.startingTicks ?? m.ticksRemaining,
          source: m.source,
        }))
      : [],
    dead: u.dead,
    get hp() { return this.totalSoldiers; },
    get atk() { return this.hp * c.atk + this.buffAtk; },
  };
  return clone;
}

export function cloneBase(b: Base): Base {
  return {
    team: b.team,
    tiles: b.tiles.map((t) => ({ x: t.x, y: t.y })),
    maxHp: b.maxHp,
    hp: b.hp,
    destroyed: b.destroyed,
  };
}

export function cloneState(s: ServerState): ServerState {
  return {
    units: s.units.map(cloneUnit),
    bases: s.bases.map(cloneBase),
    phase: s.phase,
    round: s.round,
    playerStats: { ...s.playerStats },
    aiStats: { ...s.aiStats },
    playerUsedEls: [...s.playerUsedEls],
    aiUsedEls: [...s.aiUsedEls],
    seed: s.seed ?? 0,
    // Terrain: deep-row-clone so Fire-ignition mutations during a tick
    // can't leak back into the snapshot held by the caller.
    terrainSeed: s.terrainSeed ?? 0,
    terrain: s.terrain ? s.terrain.map((row) => row.slice()) : undefined,
    // Preserve the cardMode flag across clones — `enterScheduling` + tick.ts
    // both run cloneState BEFORE executeExec validates the schedule.
    cardMode: s.cardMode,
  };
}
